import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import type { TimeEntry } from '../types';

export function useDayTimer(
  initialOpenTimer: TimeEntry | null,
  onTimerChange: (timer: TimeEntry | null) => void
) {
  const { user } = useAuth();
  const [openTimer, setOpenTimerState] = useState<TimeEntry | null>(initialOpenTimer);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sync when parent data changes (e.g. after a refresh)
  useEffect(() => {
    setOpenTimerState(initialOpenTimer);
  }, [initialOpenTimer?.id]);

  // Tick the elapsed clock
  useEffect(() => {
    if (openTimer && !openTimer.ended_at) {
      const updateElapsed = () => {
        const started = new Date(openTimer.started_at).getTime();
        setElapsedSeconds(Math.floor((Date.now() - started) / 1000));
      };
      updateElapsed();
      intervalRef.current = setInterval(updateElapsed, 1000);
    } else {
      setElapsedSeconds(0);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [openTimer?.id, openTimer?.ended_at]);

  const startDay = useCallback(async () => {
    if (!user?.id) return { error: new Error('Not logged in') };

    const { data, error } = await supabase
      .from('intern_time_entries')
      .insert({
        user_id: user.id,
        started_at: new Date().toISOString(),
        source: 'timer',
      })
      .select()
      .single();

    if (!error && data) {
      setOpenTimerState(data as TimeEntry);
      onTimerChange(data as TimeEntry);
    }
    return { error };
  }, [user?.id, onTimerChange]);

  const endDay = useCallback(async () => {
    if (!openTimer || !user?.id) return { error: new Error('No active timer') };

    const endedAt = new Date().toISOString();
    const startedMs = new Date(openTimer.started_at).getTime();
    const durationMin = Math.round((Date.now() - startedMs) / 60000);

    const { data, error } = await supabase
      .from('intern_time_entries')
      .update({ ended_at: endedAt, duration_min: durationMin })
      .eq('id', openTimer.id)
      .select()
      .single();

    if (!error && data) {
      setOpenTimerState(null);
      onTimerChange(null);
    }
    return { error, entry: data as TimeEntry };
  }, [openTimer, user?.id, onTimerChange]);

  return {
    openTimer,
    elapsedSeconds,
    isRunning: !!openTimer && !openTimer.ended_at,
    startDay,
    endDay,
  };
}

// Format seconds into HH:MM:SS
export function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

// Format minutes as "Xh Ym"
export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
